import { getFunctions, httpsCallable } from "firebase/functions";
import type { AttendanceState, CourseAttendance, CourseRun, CourseRunInput, CourseSubmission } from "../data/course";
import { firebaseApp } from "./Firebase";

const functions = getFunctions(firebaseApp, "asia-south1");

async function call<TRequest, TResponse>(name: string, data: TRequest): Promise<TResponse> {
    const result = await httpsCallable<TRequest, TResponse>(functions, name)(data);
    return result.data;
}

export type StudentCourse = {
    course: CourseRun;
    enrollmentId: string;
    submissions: CourseSubmission[];
    attendance: CourseAttendance[];
};

export type OpenCourseRun = Pick<CourseRun, "id" | "programId" | "courseTitle" | "courseType" | "programTrackKey" | "programTrackLabel" | "programTier" | "description" | "learningOutcomes" | "imageUrl" | "coverImageUrl" | "cardImageUrl" | "runTitle" | "startAt" | "endAt" | "timezone" | "modules" | "priceINR" | "capacity" | "enrollmentMode"> & {
    alreadyEnrolled: boolean;
    enrolledCount: number;
    seatsRemaining?: number;
};

export type PublicInternshipRun = Pick<OpenCourseRun, "id" | "programId" | "courseTitle" | "programTrackKey" | "programTrackLabel" | "programTier" | "description" | "learningOutcomes" | "imageUrl" | "coverImageUrl" | "cardImageUrl" | "runTitle" | "startAt" | "endAt" | "timezone" | "modules" | "priceINR" | "capacity" | "enrollmentMode" | "enrolledCount" | "seatsRemaining"> & {
    courseStatus: "published" | "completed";
};

export type PublicInternshipRuns = {
    open: PublicInternshipRun[];
    completed: PublicInternshipRun[];
};

export type PublicWorkshopRun = PublicInternshipRun;
export type PublicWorkshopRuns = { open: PublicWorkshopRun[]; completed: PublicWorkshopRun[] };

export type CourseEnrollment = {
    id: string;
    studentId?: string | null;
    name: string;
    email: string;
    status: "PENDING" | "ACTIVE" | "CANCELLED";
    source: string;
    createdAt?: string | null;
};

export type CourseRosterStudent = {
    id: string;
    name: string;
    email: string;
    enrollmentId: string;
};

export type CourseRoster = {
    course: CourseRun;
    students: CourseRosterStudent[];
    attendance: Array<CourseAttendance & { studentId: string }>;
};

export function saveCourseRun(course: CourseRunInput, courseRunId?: string) {
    return call<{ course: CourseRunInput; courseRunId?: string }, CourseRun>("saveCourseRun", { course, courseRunId });
}

export function listAdminCourseRuns() {
    return call<Record<string, never>, CourseRun[]>("listAdminCourseRuns", {});
}

export function completeCourseRun(courseRunId: string) {
    return call<{ courseRunId: string }, { ok: true; completedAt: string }>("completeCourseRun", { courseRunId });
}

export function cancelCourseRun(courseRunId: string) {
    return call<{ courseRunId: string }, { ok: true }>("cancelCourseRun", { courseRunId });
}

export function deleteDraftCourseRun(courseRunId: string) {
    return call<{ courseRunId: string }, { ok: true }>("deleteDraftCourseRun", { courseRunId });
}

export function getStudentCourses() {
    return call<Record<string, never>, StudentCourse[]>("getStudentCourses", {});
}

export function listOpenCourseRuns() {
    return call<Record<string, never>, OpenCourseRun[]>("listOpenCourseRuns", {});
}

export function listPublicInternshipRuns() {
    return call<Record<string, never>, PublicInternshipRuns>("listPublicInternshipRuns", {});
}

export function listPublicWorkshopRuns() {
    return call<Record<string, never>, PublicWorkshopRuns>("listPublicWorkshopRuns", {});
}

export function enrollInFreeCourse(courseRunId: string) {
    return call<{ courseRunId: string }, { enrollmentId: string; alreadyEnrolled?: boolean }>("enrollInFreeCourse", { courseRunId });
}

export function submitCourseAssignment(data: { courseRunId: string; moduleId: string; sessionId: string; assignmentId: string; url: string; confirmsZip: true }) {
    return call<typeof data, CourseSubmission>("submitCourseAssignment", data);
}

export function getCourseRoster(courseRunId: string) {
    return call<{ courseRunId: string }, CourseRoster>("getCourseRoster", { courseRunId });
}

export function updateCourseAttendance(data: { courseRunId: string; sessionId: string; studentId: string; status: AttendanceState }) {
    return call<typeof data, CourseAttendance & { studentId: string }>("updateCourseAttendance", data);
}

export function listCourseEnrollments(courseRunId: string) {
    return call<{ courseRunId: string }, CourseEnrollment[]>("listCourseEnrollments", { courseRunId });
}
